import { SkeletonRows } from '../../components/LoadingSkeleton/LoadingSkeleton';
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchInvoices, fetchPayments, setInvoices } from "../../features/payments/paymentSlice";
import Swal from "sweetalert2";
import { cancelInvoiceAPI, cashPayAPI } from "../../features/payments/paymentAPI";
import { Inbox } from "lucide-react";

import "./payment.css";

const Payment = () => {
  const dispatch = useDispatch();

  const { payments, loading, invoices, invoiceLoading } = useSelector(
    (state) => state.payments
  );
  const paymentRows = Array.isArray(payments) ? payments : [];
  const invoiceRows = Array.isArray(invoices) ? invoices : [];

  const authUser = useSelector((state) => state.auth?.user);
  const storedUser = JSON.parse(localStorage.getItem("admin_user") || "{}");
  const dealerId =
    authUser?.dealerId ||
    authUser?.dealerid ||
    storedUser?.dealerId ||
    storedUser?.dealerid ||
    localStorage.getItem("dealerId") ||
    "";

  const [search, setSearch] = useState("");
  const [goToPage, setGoToPage] = useState(''); 
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [invoiceSearch, setInvoiceSearch] = useState("");
  const [invoiceStatus, setInvoiceStatus] = useState("ALL");
  const [invoicePage, setInvoicePage] = useState(1);
  const itemsPerPage = 10;

  const rowsPerPage = 10;

  // ✅ Fetch payments
  useEffect(() => {
    if (dealerId) {
      dispatch(fetchPayments(dealerId));
    }
  }, [dispatch, dealerId]);


  // ✅ Reset page when search
  useEffect(() => {
    setInvoicePage(1);
  }, [invoiceStatus, invoiceSearch]);

  // ✅ Search filter
  const filtered = paymentRows.filter((item) =>
    item.client_name?.toLowerCase().includes(search.toLowerCase()) ||
    item.email?.toLowerCase().includes(search.toLowerCase()) ||
    item.mobile?.includes(search) ||
    item.status?.toLowerCase().includes(search.toLowerCase())
  );

  // ✅ Logic for Invoices (Filter, Search, Sort, Paginate)
  const getProcessedInvoices = () => {
    // 1. Filter by Status (Aligned with Badge Logic)
    let list = invoiceRows.filter((i) => {
      const isPaid = i.status === "paid" || Number(i.amount_remaining) === 0;
      if (invoiceStatus === "PAID") return isPaid;
      if (invoiceStatus === "PENDING") return !isPaid;
      return true;
    });

    // 2. Search
    if (invoiceSearch) {
      const query = invoiceSearch.toLowerCase();
      list = list.filter((i) => {
        const isPaid = i.status === "paid" || Number(i.amount_remaining) === 0;
        const statusText = isPaid ? "paid" : "pending";
        return (
          i.customer_email?.toLowerCase().includes(query) ||
          String(i.amount_due).includes(query) ||
          i.invoice_date?.toLowerCase().includes(query) ||
          statusText.includes(query)
        );
      });
    }

    // 3. Sort by Date (Oldest First)
    list = [...list].sort((a, b) => new Date(a.invoice_date) - new Date(b.invoice_date));

    return list;
  };

  const processedInvoices = getProcessedInvoices();
  const totalCount = processedInvoices.length;
  const totalInvoicePages = Math.ceil(totalCount / itemsPerPage) || 1;
  const paginatedInvoices = processedInvoices.slice(
    (invoicePage - 1) * itemsPerPage,
    invoicePage * itemsPerPage
  );

  // ✅ Pagination
  const indexOfLast = currentPage * rowsPerPage;
  const indexOfFirst = indexOfLast - rowsPerPage;
  const currentData = filtered.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filtered.length / rowsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleInvoicePageChange = (page) => {
    if (page >= 1 && page <= totalInvoicePages) {
      setInvoicePage(page);
            setGoToPage('');
    }
  };
 
 const handleInvoiceGoToPage = (e) => {
  e.preventDefault();

  const pageNum = parseInt(goToPage);

  if (
    !isNaN(pageNum) &&
    pageNum >= 1 &&
    pageNum <= totalInvoicePages
  ) {
    setInvoicePage(pageNum);
    setGoToPage("");
  }
};
  const getPageNumbers = (page, pagesCount) => {
    const pages = [];
    const maxVisiblePages = 2;
    let start = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let end = Math.min(pagesCount, start + maxVisiblePages - 1);

    if (end - start + 1 < maxVisiblePages) {
      start = Math.max(1, end - maxVisiblePages + 1);
    }

    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push('...');
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < pagesCount) {
      if (end < pagesCount - 1) pages.push('...');
      pages.push(pagesCount);
    }

    return pages;
  };

  const handleViewInvoices = (item) => {
    const custId = item.customer_id;

    setSelectedClient(item);
    setShowModal(true);

    dispatch(fetchInvoices(custId));
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedClient(null);
    handleClearInvoiceFilters();
  };

  const handleClearMainFilters = () => {
    setSearch("");
    setCurrentPage(1);
  };

  const handleClearInvoiceFilters = () => {
    setInvoiceSearch("");
    setInvoiceStatus("ALL");
    setInvoicePage(1);
  };

  const handleCancelInvoice = async (inv) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You want to cancel this invoice?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Cancel it!",
      cancelButtonText: "No",
    });

    if (!result.isConfirmed) return;

    try {
      await cancelInvoiceAPI(inv.id);

      // ✅ update UI
      const updated = invoiceRows.filter((i) => i.id !== inv.id);
      dispatch(setInvoices(updated));

      Swal.fire({
        icon: "success",
        title: "Cancelled!",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error(err);

      Swal.fire({
        icon: "error",
        title: "Failed",
        text: "Unable to cancel invoice",
      });
    }
  };

  const handleCashPay = async (inv) => {
    const result = await Swal.fire({
      title: "Confirm Cash Payment",
      text: "Mark this invoice as paid via cash?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Pay",
    });

    if (!result.isConfirmed) return;

    try {
      const payload = {
        clientid: String(selectedClient.client_id),
        customerid: String(selectedClient.customer_id),
        invoiceid: String(inv.id),
        status: "paid",
      };

      await cashPayAPI(payload);

      // ✅ update UI
      const updated = invoiceRows.map((i) =>
        i.id === inv.id
          ? { ...i, status: "paid", amount_remaining: 0 }
          : i
      );

      dispatch(setInvoices(updated));

      Swal.fire({
        icon: "success",
        title: "Paid!",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      const msg = err?.response?.data || "";

      if (JSON.stringify(msg).includes("already paid")) {
        const updated = invoiceRows.map((i) =>
          i.id === inv.id
            ? { ...i, status: "paid", amount_remaining: 0 }
            : i
        );

        dispatch(setInvoices(updated));

        Swal.fire({
          icon: "info",
          title: "Already Paid",
          timer: 1500,
          showConfirmButton: false,
        });
        return;
      }

      Swal.fire({
        icon: "error",
        title: "Failed",
        text: "Cash payment failed",
      });
    }
  };

  return (
    <div className="page">
      <h2 className="page-title">PAYMENTS</h2>

      <div className="top-bar">
        <div className="search-group">
          <input
            className="search-box"
            placeholder="Search payments..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
          />
          {search && (
            <button className="clear-btn" onClick={handleClearMainFilters}>
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>SNO</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th className="text-center">Status</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <SkeletonRows columns={6} />
            ) : currentData.length > 0 ? (
              currentData.map((item, index) => (
                <tr key={item.id}>
                  <td>{indexOfFirst + index + 1}</td>

                  <td className="name-cell">
                    <div className="avatar">
                      {item.client_name?.charAt(0)}
                    </div>
                    {item.client_name}
                  </td>

                  <td>{item.email}</td>
                  <td>{item.mobile}</td>

                  <td>
                    <span
                      className={
                        item.status === "Active"
                          ? "status-active"
                          : "status-pending"
                      }
                    >
                      {item.status}
                    </span>
                  </td>

                  <td>
                    <button
                      className="action-btn"
                      onClick={() => handleViewInvoices(item)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="payments-empty-cell">
                  <div className="payments-empty-state">
                    <span className="payments-empty-icon"><Inbox size={22} aria-hidden="true" /></span>
                    <div>
                      <strong>No payments found</strong>
                      <span>Payments will appear here when they are available.</span>
                    </div>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="pagination">
          <span className="pagination-info">
            Showing {filtered.length > 0 ? indexOfFirst + 1 : 0} to {Math.min(indexOfLast, filtered.length)} of {filtered.length}
          </span>

          <div className="page-controls">
            <button onClick={() => handlePageChange(1)} disabled={currentPage === 1}>«</button>
            <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>‹</button>
            {getPageNumbers(currentPage, totalPages).map((p, i) => (
              p === '...'
                ? <span key={i} className="page-dots">…</span>
                : <button key={p} className={currentPage === p ? 'active' : ''} onClick={() => handlePageChange(p)}>{p}</button>
            ))}
            <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0}>›</button>
            <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages || totalPages === 0}>»</button>
          </div>
        </div>
      </div>

      {/* ✅ MODAL */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal large">

            {/* HEADER */}
            <div className="modal-header">
              <h3>Invoices - {selectedClient?.client_name}</h3>

              <div className="modal-filters">
                {/* Status Filter */}
                <select
                  value={invoiceStatus}
                  onChange={(e) => setInvoiceStatus(e.target.value)}
                >
                  <option value="ALL">All</option>
                  <option value="PAID">Paid</option>
                  <option value="PENDING">Pending</option>
                </select>

                {/* Search */}
                <div className="modal-search-group">
                  <input
                    type="text"
                    placeholder="Search ..."
                    value={invoiceSearch}
                    onChange={(e) => setInvoiceSearch(e.target.value)}
                  />
                  {invoiceSearch !== "" && (
                    <button className="clear-link" onClick={() => setInvoiceSearch("")}>
                      Clear
                    </button>
                  )}
                </div>

                <span className="close-btn" onClick={handleCloseModal}>
                  ❌
                </span>
              </div>
            </div>

            {/* TABLE */}
            <div className="modal-body">
              <table className="invoice-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Amount</th>
                    <th>Date</th>
                    <th className="text-center">Status</th>
                    <th className="text-center">Actions</th>
                    <th>Cash Pay</th>
                  </tr>
                </thead>

                <tbody>
                  {invoiceLoading ? (
                    <SkeletonRows columns={6} />
                  ) : processedInvoices.length > 0 ? (
                    paginatedInvoices.map((inv) => (
                      <tr key={inv.id}>
                        <td>{inv.customer_email}</td>

                        <td>${(inv.amount_due / 100).toFixed(2)}</td>

                        <td>{inv.invoice_date}</td>

                        {/* STATUS */}
                        <td>
                          <span
                            className={
                              inv.status === "paid" ||
                                Number(inv.amount_remaining) === 0
                                ? "badge green"
                                : "badge orange"
                            }
                          >
                            {inv.status === "paid" ||
                              Number(inv.amount_remaining) === 0
                              ? "Paid"
                              : "Pending"}
                          </span>
                        </td>

                        {/* ACTIONS */}
                        <td className="actions">
                          {/* PAY NOW */}
                          {inv.status !== "paid" && Number(inv.amount_remaining) > 0 && (
                            <a
                              href={inv.hosted_invoice_url}
                              target="_blank"
                              rel="noreferrer"
                              className="btn green"
                            >
                              Pay Now
                            </a>
                          )}

                          {/* CANCEL */}
                          <button
                            className="btn red"
                            onClick={() => handleCancelInvoice(inv)}
                            disabled={
                              inv.status === "paid" || Number(inv.amount_remaining) === 0
                            }
                          >
                            Cancel
                          </button>

                          {/* PDF */}
                          <a
                            href={inv.invoice_pdf}
                            target="_blank"
                            rel="noreferrer"
                            className="icon-btn"
                          >
                            📄
                          </a>
                        </td>

                        <td>
                          {/* CASH PAY */}
                          <button
                            className="btn blue"
                            onClick={() => handleCashPay(inv)}
                            disabled={
                              inv.status === "paid" || Number(inv.amount_remaining) === 0
                            }
                          >
                            Cash Pay
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6">No invoices found</td>
                    </tr>
                  )}
                </tbody>
              </table>
              <div className="pagination-info invoice-pagination-info">
                Showing{" "}
                {totalCount > 0 ? (invoicePage - 1) * itemsPerPage + 1 : 0} -{" "}
                {Math.min(invoicePage * itemsPerPage, totalCount)} of{" "}
                {totalCount}
              </div>
              {/* PAGINATION */}
              <div className="pagination">
                <div className="page-controls">
                 <button onClick={() => handleInvoicePageChange(1)} disabled={invoicePage === 1}>«</button>
                <button onClick={() => handleInvoicePageChange(invoicePage - 1)} disabled={invoicePage === 1}>‹</button>
                  {getPageNumbers(invoicePage, totalInvoicePages).map((p, i) => (
                    p === '...'
                      ? <span key={i} className="page-dots">...</span>
                      : <button key={p} className={invoicePage === p ? 'active' : ''} onClick={() => handleInvoicePageChange(p)}>{p}</button>
                  ))}
                  <button onClick={() => handleInvoicePageChange(invoicePage + 1)} disabled={invoicePage >= totalInvoicePages}>›</button>
                  <button onClick={() => handleInvoicePageChange(totalInvoicePages)} disabled={invoicePage >= totalInvoicePages}>»</button> 
                </div>
                <div className="goto-wrapper">
                <form onSubmit={handleInvoiceGoToPage}>
                  <input
                    type="number"
                    min="1"
                    max={totalInvoicePages}
                    value={goToPage}
                    onChange={(e) => setGoToPage(e.target.value)}
                    placeholder="Go to"
                  />
                  <button type="submit">Go</button>
                </form>
              </div>
              </div>
              
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payment;
